import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Award } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import CertLightbox from './CertLightbox';
import { certificationsData, certificationStats } from '../data/certificationsData';

const certifications = certificationsData;

const certCategories = [
  { titleKey: 'All', count: certifications.length },
  { titleKey: 'Data & AI', count: certifications.filter(c => c.category === 'Data & AI').length },
  { titleKey: 'IT & Programming', count: certifications.filter(c => c.category === 'IT & Programming').length },
  { titleKey: 'Language', count: certifications.filter(c => c.category === 'Language').length },
  { titleKey: 'Other', count: certifications.filter(c => c.category === 'Other').length },
];

function CertCard({ cert, index, onClick }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.3, delay: index * 0.05 }}
      whileHover={{ y: -4 }}
      onClick={() => onClick(cert)}
      className="group relative rounded-2xl overflow-hidden glass glass-hover cursor-pointer"
    >
      <div className="relative h-44 overflow-hidden">
        {cert.images && cert.images.length > 0 ? (
          <img
            src={cert.images[0]}
            alt={cert.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full bg-linear-to-br from-accent-500/20 to-cyan-500/20 flex items-center justify-center">
            <Award size={32} className="text-accent-400/60" />
          </div>
        )}
        <div className="absolute inset-0 overlay-gradient" />

        {cert.highlight && (
          <div className="absolute top-3 right-3 w-8 h-8 rounded-full bg-accent-500/80 backdrop-blur-sm flex items-center justify-center">
            <Award size={16} className="text-white" />
          </div>
        )}
      </div>

      <div className="p-4">
        <h3 className="text-heading font-semibold text-sm mb-1 group-hover:text-accent-400 transition-colors line-clamp-2">
          {cert.title}
        </h3>
        <p className="text-muted text-xs">{cert.issuer}</p>
      </div>
    </motion.div>
  );
}

export default function Certifications() {
  const [selectedCert, setSelectedCert] = useState(null);
  const [activeCategory, setActiveCategory] = useState('All');
  const { t } = useLanguage();

  const filteredCerts = activeCategory === 'All'
    ? certifications
    : certifications.filter(c => c.category === activeCategory);

  return (
    <section id="certifications" className="relative py-24 overflow-hidden">
      <div className="absolute top-0 left-1/3 w-96 h-96 bg-accent-500/5 rounded-full blur-3xl" />

      <div className="section-container relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <p className="text-accent-400 font-mono text-sm mb-2">{t.certifications.subtitle}</p>
          <h2 className="text-4xl sm:text-5xl font-bold text-heading mb-4">
            <span className="gradient-text">{t.certifications.title}</span>
          </h2>
          <div className="w-24 h-1 bg-linear-to-r from-accent-500 to-cyan-500 mx-auto rounded-full" />
        </motion.div>

        {/* Stats */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="flex flex-wrap justify-center gap-6 mb-10"
        >
          {[
            { value: certificationStats.certificateCount, label: t.certifications.stats.certificates },
            { value: certificationStats.toeflScore, label: t.certifications.stats.toefl },
            { value: certificationStats.institutionsCount, label: t.certifications.stats.institutions },
          ].map((stat) => (
            <div key={stat.label} className="text-center px-6 py-4 glass rounded-xl">
              <div className="text-2xl font-bold gradient-text">{stat.value}</div>
              <div className="text-xs text-muted mt-1">{stat.label}</div>
            </div>
          ))}
        </motion.div>

        <div className="mb-10">
          <div className="tabs-scroll-wrap">
            <div className="tabs-scroll">
              <div className="tabs-scroll-inner glass p-1.5 rounded-xl mx-auto">
                {certCategories.map((category) => (
                  <button
                    key={category.titleKey}
                    onClick={() => setActiveCategory(category.titleKey)}
                    className={`relative px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors duration-300 ${
                      activeCategory === category.titleKey
                        ? 'text-heading'
                        : 'text-muted hover:text-heading'
                    }`}
                  >
                    {activeCategory === category.titleKey && (
                      <motion.div
                        layoutId="activeCertTab"
                        className="absolute inset-0 bg-accent-500/10 rounded-lg"
                        transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                      />
                    )}
                    <span className="relative z-10">
                      {t.certifications.categories[category.titleKey]} ({category.count})
                    </span>
                  </button>
                ))}
              </div>
            </div>
            <div className="tabs-scroll-hint" aria-hidden="true">
              <span className="tabs-scroll-hint-thumb" />
            </div>
          </div>
        </div>

        <motion.div 
          className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4"
          key={activeCategory}
        >
          <AnimatePresence>
            {filteredCerts.map((cert, index) => (
              <CertCard
                key={cert.title}
                cert={cert}
                index={index}
                onClick={setSelectedCert}
              />
            ))}
          </AnimatePresence>
        </motion.div>
      </div>

      {/* Certificate Lightbox */}
      <AnimatePresence>
        {selectedCert && (
          <CertLightbox
            cert={selectedCert}
            onClose={() => setSelectedCert(null)}
          />
        )}
      </AnimatePresence>
    </section>
  );
}
